/**
 * @file Client du serveur de jeu (copie de GameServer/src/sdk/GameClient.ts, aucune dependance) : authentification avec rafraichissement automatique
 * du jeton, appels REST types, socket temps reel avec requetes correlees + salons. `baseUrl` vide = meme origine que la page (nginx proxifie /v1).
 */

export interface TokenStore {
  get(): Session | null;
  set(s: Session | null): void;
}

export interface Session {
  token: string;
  refreshToken: string;
  expiresAt: number;
  userId: string;
}

export class GameApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'GameApiError';
  }
}

export interface GameClientOptions {
  /** Ex. "https://catv2.apilow.com" ; vide = meme origine que la page. */
  baseUrl: string;
  gameId: string;
  /** Cle d'API du jeu ; facultative en meme origine (nginx l'ajoute) et obligatoire pour l'application native. */
  apiKey?: string;
  /** Persistance du jeton (localStorage cote navigateur) ; memoire par defaut. */
  store?: TokenStore;
  fetchImpl?: typeof fetch;
  WebSocketImpl?: typeof WebSocket;
}

type Json = Record<string, unknown>;

export class GameClient {
  private session: Session | null;
  private readonly store: TokenStore;
  private readonly f: typeof fetch;

  constructor(readonly opts: GameClientOptions) {
    let mem: Session | null = null;
    this.store = opts.store ?? { get: () => mem, set: (s) => void (mem = s) };
    this.session = this.store.get();
    this.f = opts.fetchImpl ?? ((input, init) => fetch(input, init));
  }

  get userId(): string | null {
    return this.session?.userId ?? null;
  }

  get signedIn(): boolean {
    return this.session !== null;
  }

  // ------------------------------------------------------------------ transport

  private async raw(method: string, path: string, body: unknown, auth: 'user' | 'apikey'): Promise<unknown> {
    const headers: Record<string, string> = { 'x-game-id': this.opts.gameId };
    if (auth === 'apikey' && this.opts.apiKey) headers['x-api-key'] = this.opts.apiKey;
    else if (this.session) headers['authorization'] = `Bearer ${this.session.token}`;
    if (body !== undefined) headers['content-type'] = 'application/json';
    const res = await this.f(`${this.opts.baseUrl}${path}`, { method, headers, ...(body !== undefined ? { body: JSON.stringify(body) } : {}) });
    if (res.status === 204) return undefined;
    const text = await res.text();
    let data: { error?: { code: string; message: string; details?: unknown } } = {};
    try {
      data = text ? (JSON.parse(text) as typeof data) : {};
    } catch {
      // reponse non JSON (page d'erreur d'un proxy) : l'etat HTTP suffit
    }
    if (!res.ok) {
      const e = data.error;
      throw new GameApiError(res.status, e?.code ?? 'http_error', e?.message ?? `HTTP ${res.status}`, e?.details);
    }
    return data;
  }

  /** Appel authentifie ; sur 401, tente un rafraichissement du jeton puis rejoue une fois. */
  async request<T = Json>(method: string, path: string, body?: unknown): Promise<T> {
    try {
      return (await this.raw(method, path, body, 'user')) as T;
    } catch (e) {
      if (e instanceof GameApiError && e.status === 401 && this.session) {
        await this.refresh();
        return (await this.raw(method, path, body, 'user')) as T;
      }
      throw e;
    }
  }

  // ------------------------------------------------------------------ authentification

  private async auth(kind: string, body: Json): Promise<Session & { created: boolean; username: string }> {
    const r = (await this.raw('POST', `/v1/auth/${kind}`, body, 'apikey')) as Session & { created: boolean; username: string };
    this.setSession({ token: r.token, refreshToken: r.refreshToken, expiresAt: r.expiresAt, userId: r.userId });
    return r;
  }

  private setSession(s: Session | null): void {
    this.session = s;
    this.store.set(s);
  }

  authDevice(deviceId: string, o: { username?: string; create?: boolean } = {}) {
    return this.auth('device', { id: deviceId, ...o });
  }
  authEmail(email: string, password: string, o: { username?: string; create?: boolean } = {}) {
    return this.auth('email', { id: email, password, ...o });
  }
  authCustom(id: string, o: { username?: string; create?: boolean } = {}) {
    return this.auth('custom', { id, ...o });
  }

  async refresh(): Promise<void> {
    if (!this.session) throw new GameApiError(401, 'unauthorized', 'Pas de session');
    try {
      const r = (await this.raw('POST', '/v1/auth/refresh', { refreshToken: this.session.refreshToken }, 'apikey')) as Session;
      this.setSession({ token: r.token, refreshToken: r.refreshToken, expiresAt: r.expiresAt, userId: r.userId });
    } catch (e) {
      this.setSession(null);
      throw e;
    }
  }

  async logout(): Promise<void> {
    if (this.session) await this.request('POST', '/v1/auth/logout').catch(() => undefined);
    this.setSession(null);
  }

  // ------------------------------------------------------------------ API metier (fines enveloppes)

  account = () => this.request('GET', '/v1/account');
  updateAccount = (patch: Json) => this.request('PUT', '/v1/account', patch);

  pullSave = () => this.request<{ found: boolean; revision: number; payload?: string; savedAtMs?: number; checksum?: string }>('GET', '/v1/save');
  pushSave = (input: { baseRevision: number; payload: string; savedAtMs?: number; checksum?: string; force?: boolean }) => this.request<{ accepted: true; revision: number; checksum: string }>('PUT', '/v1/save', input);

  wallet = () => this.request<{ balances: Record<string, number>; inventory: Record<string, number> }>('GET', '/v1/wallet');
  catalog = () => this.request('GET', '/v1/economy/catalog');
  purchase = (storeId: string, offerId: string, idempotencyKey?: string) => this.request('POST', '/v1/economy/purchase', { storeId, offerId, idempotencyKey });

  leaderboards = () => this.request('GET', '/v1/leaderboards');
  submitScore = (boardId: string, score: number, subscore?: number) => this.request('POST', `/v1/leaderboards/${encodeURIComponent(boardId)}/records`, { score, subscore });
  records = (boardId: string, o: { limit?: number; cursor?: string; period?: string } = {}) => this.request('GET', `/v1/leaderboards/${encodeURIComponent(boardId)}/records?${new URLSearchParams(Object.entries(o).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)]))}`);
  aroundMe = (boardId: string, limit = 11) => this.request('GET', `/v1/leaderboards/${encodeURIComponent(boardId)}/around-me?limit=${limit}`);

  liveops = () => this.request('GET', '/v1/liveops/state');
  identify = (props: { defaults?: Json; custom?: Json }) => this.request('POST', '/v1/liveops/identify', props);
  claimEvent = (id: string) => this.request('POST', `/v1/liveops/events/${encodeURIComponent(id)}/claim`);
  sendEvents = (events: Array<{ name: string; value?: number; props?: Json }>) => this.request('POST', '/v1/analytics/events', { events });
  achievements = () => this.request('GET', '/v1/achievements');
  claimAchievement = (id: string) => this.request('POST', `/v1/achievements/${encodeURIComponent(id)}/claim`);

  inbox = () => this.request('GET', '/v1/inbox');
  claimMail = (id: string) => this.request('POST', `/v1/inbox/${encodeURIComponent(id)}/claim`);

  friends = () => this.request('GET', '/v1/friends');
  addFriend = (target: { userId?: string; username?: string }) => this.request('POST', '/v1/friends', target);
  acceptFriend = (id: string) => this.request('POST', `/v1/friends/${encodeURIComponent(id)}/accept`);

  rooms = (type?: string) => this.request('GET', `/v1/rooms${type ? `?type=${encodeURIComponent(type)}` : ''}`);
  rpc = <T = unknown>(name: string, payload?: unknown) => this.request<T>('POST', `/v1/rpc/${encodeURIComponent(name)}`, payload ?? {});

  // ------------------------------------------------------------------ temps reel

  /** Ouvre la socket temps reel (le jeton doit etre valide). */
  async connect(): Promise<RealtimeSocket> {
    if (!this.session) throw new GameApiError(401, 'unauthorized', 'Pas de session');
    if (this.session.expiresAt - 5000 < Date.now()) await this.refresh();
    const WS = this.opts.WebSocketImpl ?? WebSocket;
    const origin = this.opts.baseUrl || window.location.origin;
    const url = `${origin.replace(/^http/, 'ws')}/v1/ws?token=${encodeURIComponent((this.session as Session).token)}`;
    const sock = new RealtimeSocket(new WS(url));
    await sock.ready;
    return sock;
  }
}

export type SocketListener = (msg: Json) => void;

export class RealtimeSocket {
  private nextId = 1;
  private readonly pending = new Map<number, { resolve: (v: unknown) => void; reject: (e: Error) => void }>();
  private readonly listeners = new Map<string, Set<SocketListener>>();
  readonly ready: Promise<Json>;
  private onClose: ((code: number, reason: string) => void) | null = null;

  constructor(private readonly ws: WebSocket) {
    this.ready = new Promise((resolve, reject) => {
      this.on('welcome', (m) => resolve(m));
      ws.addEventListener('error', () => reject(new Error('Connexion WebSocket impossible')));
      ws.addEventListener('close', (ev) => reject(new Error(`Socket fermee (${ev.code})`)));
    });
    this.ready.catch(() => undefined);
    ws.addEventListener('message', (ev) => this.dispatch(String(ev.data)));
    ws.addEventListener('close', (ev) => {
      for (const p of this.pending.values()) p.reject(new Error('Socket fermee'));
      this.pending.clear();
      this.onClose?.(ev.code, ev.reason);
    });
  }

  private dispatch(raw: string): void {
    const msg = JSON.parse(raw) as Json;
    if (msg['t'] === 'res') {
      const p = this.pending.get(msg['id'] as number);
      if (!p) return;
      this.pending.delete(msg['id'] as number);
      if (msg['ok'] === true) p.resolve(msg['d']);
      else {
        const e = msg['error'] as { code: string; message: string };
        p.reject(new GameApiError(400, e.code, e.message));
      }
      return;
    }
    for (const l of this.listeners.get(String(msg['t'])) ?? []) l(msg);
    for (const l of this.listeners.get('*') ?? []) l(msg);
  }

  /** Ecoute un type de message serveur (`'*'` = tous). Retourne la fonction de desinscription. */
  on(type: string, fn: SocketListener): () => void {
    const set = this.listeners.get(type) ?? new Set();
    set.add(fn);
    this.listeners.set(type, set);
    return () => set.delete(fn);
  }

  closed(fn: (code: number, reason: string) => void): void {
    this.onClose = fn;
  }

  /** Envoie une requete et attend la reponse correlee. */
  request<T = unknown>(type: string, payload: Json = {}): Promise<T> {
    const id = this.nextId++;
    return new Promise<T>((resolve, reject) => {
      this.pending.set(id, { resolve: resolve as (v: unknown) => void, reject });
      this.ws.send(JSON.stringify({ t: type, id, ...payload }));
    });
  }

  send(type: string, payload: Json = {}): void {
    this.ws.send(JSON.stringify({ t: type, ...payload }));
  }

  // salons
  joinRoom<T = Json>(type: string, o: { roomId?: string; code?: string; friendId?: string; shard?: string; private?: boolean } = {}): Promise<T> {
    return this.request<T>('room.join', { type, ...o });
  }
  leaveRoom(roomId: string): Promise<unknown> {
    return this.request('room.leave', { roomId });
  }
  /** Message vers le gestionnaire du salon (sans accuse : les reponses arrivent comme messages `room.msg`). */
  roomSend(roomId: string, d: Json): void {
    this.send('room.msg', { roomId, d });
  }
  /** Ecoute les messages d'un salon : `fn(d, roomId)`. */
  onRoom(fn: (d: Json, roomId: string) => void): () => void {
    return this.on('room.msg', (m) => fn(m['d'] as Json, String(m['roomId'])));
  }

  close(): void {
    this.ws.close();
  }
}
